import { Component, OnInit, ViewChild } from '@angular/core';
import { Carrier } from '../user-dashboard/shared/carrier.model';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../shared/services/authentication.service';
import { CarrierService } from '../user-dashboard/shared/carrier.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../shared/services/loader.service';
import { NavigationService } from '../shared/services/navigation.service';
import { ErrorHandlerService } from '../shared/services/error-handler.service';
import { combineLatest, of } from 'rxjs';
import { finalize, mergeMap, tap } from 'rxjs/operators';
import { LOCAL_STORAGE_KEYS } from '../core/constants';
import { NgForm } from '@angular/forms';
import { WebhookSubscriptionsComponent } from './webhook-subscriptions/webhook-subscriptions.component';
import { WebhookEventSearchResult } from './shared/webhook-event.model';
import { DocumentationAnchor, DocumentationTab } from './webhook-documentation/webhook-documentation.component';
import { Location } from '@angular/common';
import { WebhookEventService } from './shared/webhook-event.service';

@Component({
  selector: 'app-webhooks-events',
  templateUrl: './webhooks-events.component.html',
  styleUrls: ['./webhooks-events.component.scss']
})
export class WebhooksEventsComponent implements OnInit {

  public targetInDocumentation: { tab: DocumentationTab, anchor: DocumentationAnchor };
  public carriers: Carrier[] = [];
  public selectedCarrier: Carrier;
  public selectedEventId: string;
  private canShowDebug       = false;

  @ViewChild('webhookSubscriptionsComponent')
  private webhookSubscriptionsComponent: WebhookSubscriptionsComponent;

  listSelected = true;
  configSelected = true;
  docSelected = true;

  goToList = () => this.location.go(this.navigation.webhooksEvents().overall().getUri());
  goToDetails = (eventId: string) => this.location.go(this.navigation.webhooksEvents().details(eventId).getUri());
  goToConfig = () => this.location.go(this.navigation.webhooksEvents().config().getUri());
  goToDoc = (tab?: DocumentationTab, anchor?: DocumentationAnchor) => this.location.go(this.navigation.webhooksEvents().doc(tab, anchor).getUri());

  constructor(
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private carrierService: CarrierService,
    private toastr: ToastrService,
    private loader: LoaderService,
    private navigation: NavigationService,
    private errorHandler: ErrorHandlerService,
    private webhookEventService: WebhookEventService,
    private location: Location,
  ) {

    if (this.authService.canMonitorApi()) {
      this.canShowDebug = true;
    }

    if (this.authService.canManageAllCarriersWebhooks()) {
      this.fetchCarriers();
    } else if (this.authService.canManageCarrierWebhooks()) {
      this.fetchCarrier();
    } else {
      toastr.error('You are not allowed to view this page');
      navigation.home().go();
      return;
    }
  }

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.data])
      .subscribe(([params, data]) => {
        this.listSelected = false;
        this.configSelected = false;
        this.docSelected = false;

        if (data.mode === 'config') {
          this.configSelected = true;
        } else if (data.mode === 'doc') {
          this.docSelected = true;
        } else if (data.mode === 'details') {
          this.listSelected = true;
          this.selectedEventId = params.id;
        } else {
          this.listSelected = true;
        }
      });
  }

  private fetchCarriers(): void {
    of({})
      .pipe(
        tap(() => this.loader.startLoad()),
        mergeMap(() => this.carrierService.getAll()),
        finalize(() => this.loader.stopLoad()),
      )
      .subscribe(carriers => {
        this.carriers = carriers;
        if (this.selectedEventId) {
          this.webhookEventService.getEventById(this.selectedEventId).subscribe(event => {
            this.selectedCarrier = carriers.find((carrier) => carrier.id === event.subscriberId);
          }, error => this.errorHandler.handleHttpError(error, 'webhooks-events.details.errors.fetch-event-by-id'));
        } else if (localStorage.getItem(LOCAL_STORAGE_KEYS.WEBHOOK_EVENTS_CARRIER)) {
            this.selectedCarrier = carriers.find((carrier) => carrier.id === localStorage.getItem(LOCAL_STORAGE_KEYS.WEBHOOK_EVENTS_CARRIER));
        }
      }, e => this.errorHandler.handleHttpError(e, 'webhooks-events.errors.fetch-carriers'));
  }

  private fetchCarrier(): void {
    this.loader.startLoad();
    this.carrierService.getSelfCarrier()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(carrier => {
        this.carriers        = [carrier];
        this.selectedCarrier = carrier;
      }, e => this.errorHandler.handleHttpError(e, 'webhooks-events.errors.fetch-self-carrier'));
  }

  public onSelectedCarrierChange(selectedCarrier: Carrier): void {
    this.selectedCarrier = selectedCarrier;
    this.resetEvent();
    localStorage.setItem(LOCAL_STORAGE_KEYS.WEBHOOK_EVENTS_CARRIER, selectedCarrier ? selectedCarrier.id : undefined);
  }

  public getForm(): NgForm {
    return this.webhookSubscriptionsComponent ? this.webhookSubscriptionsComponent.webhookSubscriptionForm : undefined;
  }

  public selectEvent(selectedEvent: WebhookEventSearchResult): void {
    this.goToDetails(selectedEvent.id);
    this.selectedEventId = selectedEvent.id;
    this.listSelected = true;
    this.configSelected = false;
    this.docSelected = false;
  }

  public resetEvent(): void {
    this.goToList();
    this.selectedEventId = null;
    this.listSelected = true;
    this.configSelected = false;
    this.docSelected = false;
  }

  displayDocumentation(targetInDocumentation: { tab: DocumentationTab, anchor: DocumentationAnchor }) {
    this.goToDoc(targetInDocumentation.tab, targetInDocumentation.anchor);
    this.selectedEventId = null;
    this.listSelected = false;
    this.configSelected = false;
    this.docSelected = true;
    this.targetInDocumentation = targetInDocumentation;
  }
}
